/**
 * Die Zustimmungsseite des Connectors: /connect?authorization_id=…
 *
 * Englischer Pfad, obwohl die App deutsch spricht — nach derselben Regel wie
 * /report: was in einem Maschinen-Ablauf auftaucht oder weitergeleitet wird,
 * ist englisch; was ein angemeldeter Kunde selbst anklickt, bleibt deutsch.
 * Diese Seite erscheint mitten in einem OAuth-Fluss, dessen Umgebung in Claude
 * englisch ist. `/verbinde` bleibt erreichbar, solange der Authorization Path
 * in Supabase noch dorthin zeigt.
 *
 * Supabases OAuth-Server ist „headless" — für die Zustimmung leitet er auf
 * eine Seite der eigenen App. Hier meldet sich der Nutzer an, sieht, wer
 * Zugriff will, und entscheidet.
 *
 * WARUM SIE HIERHER GEHÖRT: Sie lag in `Blessing-Digital/99takes-website`,
 * also einer Produkt-Oberfläche in der Agentur-Organisation — dieselbe Naht
 * wie die geteilte Datenbank, eine Ebene höher.
 *
 * WAS DER UMZUG GEWINNT: Dort war sie eine reine Anmeldemaske und musste
 * jeden nach seinem Passwort fragen, auch jemanden, der gerade in der App
 * eingeloggt war. Hier gibt es bereits eine Sitzung. Wer angemeldet ist, sieht
 * sofort die Zustimmung — eine Passwortabfrage weniger, und eine Stelle
 * weniger, an der jemand sein Passwort eintippt.
 *
 * KEIN APP-RAHMEN und keine Weiterleitung durch den Riegel: Diese Seite ist
 * ein einmaliger Schritt in einem fremden Ablauf, kein Ort in der App.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { freigabeHinweis, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '../config';
import { fehlertext, supabase } from '../supabase';
import { useSitzung } from '../session';
import { Feld, Hinweis, Knopf, Laedt, Rahmen, Verweis } from '../ui';
import { t } from '../sprache';
import { useTurnstile } from '../turnstile';

/** Was ein Scope in Worten bedeutet. Was hier fehlt, steht roh da — besser
 *  als eine erfundene Umschreibung für eine Berechtigung. */
const SCOPE_TEXT: Record<string, string> = {
  openid: 'erkennen, dass du es bist',
  profile: 'deinen Namen lesen',
  email: 'deine E-Mail-Adresse lesen',
  phone: 'deine Telefonnummer lesen',
  offline_access: 'verbunden bleiben, ohne dass du dich jedes Mal neu anmeldest',
};
const SCOPE_TEXT_EN: Record<string, string> = {
  openid: 'recognize that it is you',
  profile: 'read your name',
  email: 'read your email address',
  phone: 'read your phone number',
  offline_access: 'stay connected without you signing in every time',
};

/** Was die Zustimmung öffnet, in beiden Sprachen. Gewählt beim Rendern. */
const DARF_DE = [
  'Texte und Seiten gegen deine Personas prüfen (verbraucht Credits aus deinem Guthaben)',
  'Zielgruppen anlegen (kostet Credits) und ändern',
  'Texte schreiben (verbraucht Credits)',
  'Wettbewerberseiten lesen und im Netz nach Kundenstimmen suchen',
  'Berichte veröffentlichen, die danach über einen Link erreichbar sind',
  'Guthaben und Trefferquote lesen',
];
const DARF_EN = [
  'Test copy and pages against your personas (uses credits from your balance)',
  'Create audiences (costs credits) and change them',
  'Write copy (uses credits)',
  'Read competitor pages and search the web for customer voices',
  'Publish reports that are then reachable through a link',
  'Read your balance and hit rate',
];

type Stufe = 'laedt' | 'anmelden' | 'zustimmen' | 'fertig' | 'kein-link';

export default function Verbinden() {
  const [params] = useSearchParams();
  const authorizationId = (params.get('authorization_id') ?? '').trim();
  const { status, session } = useSitzung();

  const [stufe, setStufe] = useState<Stufe>(authorizationId ? 'laedt' : 'kein-link');
  const [anwendung, setAnwendung] = useState(() => t('Eine Anwendung', 'An application'));
  const [scopes, setScopes] = useState<string[]>([]);
  const [konto, setKonto] = useState('');
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  // Nur das Anmeldeformular braucht den Token; wer schon angemeldet ist,
  // sieht die Zustimmung, und dort entsteht kein Widget.
  const pruefung = useTurnstile('connect');

  const kopf = useCallback((token: string | null): Record<string, string> => ({
    apikey: SUPABASE_PUBLISHABLE_KEY,
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), []);

  /** Details der Anfrage holen. 401 heißt: erst anmelden. */
  const detailsLaden = useCallback(async (token: string | null) => {
    if (!authorizationId) return;
    setStufe('laedt');
    setFehler('');
    try {
      const res = await fetch(
        `${SUPABASE_URL}/auth/v1/oauth/authorizations/${encodeURIComponent(authorizationId)}`,
        { headers: kopf(token) },
      );
      if (res.status === 401) { setStufe('anmelden'); return; }
      if (!res.ok) throw new Error(`status ${res.status}`);
      const d = await res.json();
      setAnwendung(d?.client?.client_name || d?.client?.name || t('Eine Anwendung', 'An application'));
      const roh = typeof d?.scope === 'string' ? d.scope.split(/\s+/) : (d?.scopes ?? []);
      const liste = (roh as string[]).filter(Boolean);
      setScopes(liste.length ? liste : ['openid']);
      if (d?.user?.email) setKonto(d.user.email);
      setStufe('zustimmen');
    } catch {
      setStufe('zustimmen');
      setFehler(t('Die Anfrage konnte nicht geladen werden. Starte die Verbindung in Claude noch einmal.', 'The request could not be loaded. Start the connection in Claude again.'));
    }
  }, [authorizationId, kopf]);

  // Erster Versuch mit der Sitzung, die die App ohnehin hat. Gibt es keine,
  // kommt 401 und das Formular erscheint — wie vorher, nur ohne die
  // Passwortabfrage für alle, die schon angemeldet sind.
  useEffect(() => {
    if (!authorizationId || status === 'laedt') return;
    void detailsLaden(session?.access_token ?? null);
  }, [authorizationId, status, session, detailsLaden]);

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    pruefung.ausfuehren(anmelden);
  }

  async function anmelden(captchaToken: string) {
    setLaeuft(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: passwort, options: { captchaToken } });
    setLaeuft(false);
    if (error || !data.session) {
      setFehler(fehlertext(error));
      return;
    }
    await detailsLaden(data.session.access_token);
  }

  async function entscheiden(action: 'approve' | 'deny') {
    setFehler('');
    setLaeuft(true);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(
        `${SUPABASE_URL}/auth/v1/oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`,
        { method: 'POST', headers: kopf(data.session?.access_token ?? null), body: JSON.stringify({ action }) },
      );
      const j = await res.json().catch(() => null);
      if (!res.ok || !j?.redirect_url) throw new Error('keine Rückleitung');
      setStufe('fertig');
      // Zurück in den Ablauf, aus dem der Nutzer kam. Kein Router: das Ziel
      // liegt außerhalb dieser App.
      window.location.href = j.redirect_url;
    } catch {
      setLaeuft(false);
      setFehler(t('Das hat nicht geklappt. Starte die Verbindung in Claude noch einmal.', 'That did not work. Start the connection in Claude again.'));
    }
  }

  if (stufe === 'kein-link') {
    return (
      <Rahmen
        titel={t('Der Link ist unvollständig', 'The link is incomplete')}
        unter={t(
          'In der Adresse fehlt die Kennung der Anfrage. Das passiert, wenn die Seite direkt aufgerufen wird statt aus dem Verbindungsvorgang heraus.',
          'The address is missing the ID of the request. This happens when the page is opened directly instead of from the connection flow.',
        )}
      >
        <Hinweis>{t('Starte die Verbindung in Claude noch einmal, unter Anpassungen → Konnektoren bei 99takes.', 'Start the connection in Claude again, under Customize → Connectors at 99takes.')}</Hinweis>
      </Rahmen>
    );
  }

  if (stufe === 'laedt') {
    return <Rahmen titel={t('Einen Moment', 'One moment')}><Laedt>{t('Die Anfrage wird geprüft.', 'Checking the request.')}</Laedt></Rahmen>;
  }

  if (stufe === 'fertig') {
    return (
      <Rahmen titel={t('Erledigt', 'Done')} unter={t('Du wirst zurückgeleitet.', 'You are being redirected.')}>
        <Hinweis art="gut">{t(
          'Wenn nichts passiert, kehre zu Claude zurück. Öffne dort einen neuen Chat: ein offener kennt die Werkzeuge noch nicht.',
          'If nothing happens, go back to Claude. Open a new chat there: an open one does not know the tools yet.',
        )}</Hinweis>
        <p className="mt-3 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">{freigabeHinweis()}</p>
      </Rahmen>
    );
  }

  if (stufe === 'anmelden') {
    return (
      <Rahmen
        titel={t('Bei 99takes anmelden', 'Sign in to 99takes')}
        unter={t(
          'Eine Anwendung möchte sich mit deinem 99takes-Konto verbinden. Melde dich an, damit wir wissen, für welches. Auch wenn du dein Konto gerade erst angelegt hast: Dieses Fenster kennt deine Anmeldung noch nicht.',
          'An application wants to connect to your 99takes account. Sign in so we know which one. Even if you just created your account: this window does not know your sign-in yet.',
        )}
        fuss={<>{t('Noch kein Konto?', 'No account yet?')} <Verweis to="/register">{t('Konto anlegen', 'Create account')}</Verweis></>}
      >
        <form onSubmit={absenden}>
          {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
          <Feld label={t('E-Mail', 'Email')} type="email" autoComplete="email" required autoFocus
            value={email} onChange={e => setEmail(e.target.value)} disabled={laeuft} />
          <Feld label={t('Passwort', 'Password')} type="password" autoComplete="current-password" required
            value={passwort} onChange={e => setPasswort(e.target.value)} disabled={laeuft} />
          {pruefung.feld}
          <Knopf type="submit" laedt={laeuft || pruefung.wartet}>{t('Anmelden', 'Sign in')}</Knopf>
        </form>
      </Rahmen>
    );
  }

  return (
    <Rahmen
      titel={t('Zugriff erlauben?', 'Allow access?')}
      unter={t(
        <><strong className="text-text">{anwendung}</strong> möchte sich mit deinem 99takes-Konto verbinden.</>,
        <><strong className="text-text">{anwendung}</strong> wants to connect to your 99takes account.</>,
      )}
      fuss={t(
        'Du kannst den Zugriff jederzeit beenden: in Claude unter Anpassungen → Konnektoren oder indem du dich bei uns meldest.',
        'You can end access at any time: in Claude under Customize → Connectors, or by contacting us.',
      )}
    >
      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}

      {konto && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-linie bg-grund px-3.5 py-2.5 text-[0.875rem]">
          <span className="min-w-0 text-text-leise">
            {t('Angemeldet als', 'Signed in as')} <strong className="text-text [overflow-wrap:anywhere]">{konto}</strong>
          </span>
          {/* Wer mit dem falschen Konto angemeldet ist — privat statt Firma —
              soll hier wechseln koennen, statt den Zugriff dem falschen
              Konto zu geben. Abmelden laesst die Anfrage stehen; die Seite
              faellt auf das Anmeldeformular zurueck. */}
          <button
            type="button" disabled={laeuft}
            onClick={() => { setKonto(''); void supabase.auth.signOut(); }}
            className="cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke"
          >
            {t('Anderes Konto', 'Other account')}
          </button>
        </div>
      )}

      {/*
        WARUM DIESE LISTE HIER STEHT: Die Scopes darunter sind, was der
        Auth-Server anfragt — „erkennen, dass du es bist". Was die Zustimmung
        tatsaechlich oeffnet, ist der ganze MCP fuer dieses Konto, inklusive
        Laeufen, die Credits kosten, und Berichten, die ueber einen Link
        erreichbar sind. Eine Zustimmungsseite, die nur die harmlose Haelfte
        nennt, holt eine Zustimmung ein, die niemand gegeben hat. Derselbe
        Umfang steht unter /keys bei „Was ein Schluessel darf".
      */}
      <p className="mt-0 mb-2 text-[0.9375rem] font-medium text-tinte">{t(<>Danach kann {anwendung} für dich:</>, <>After that, {anwendung} can do this for you:</>)}</p>
      <ul className="mt-0 mb-4 list-none space-y-1.5 p-0 text-[0.9375rem] leading-relaxed text-text">
        {t(DARF_DE, DARF_EN).map(v => (
          <li key={v} className="grid grid-cols-[1rem_1fr] gap-2">
            <span aria-hidden className="mt-[0.45rem] size-1.5 rounded-full bg-marke" />
            {v}
          </li>
        ))}
      </ul>
      <p className="mt-0 mb-6 text-[0.8125rem] leading-relaxed text-text-hell">
        {t(
          <>Bevor etwas Credits kostet, nennt Claude den Preis. Dein Konto ändern oder
        Schlüssel anlegen kann {anwendung} nicht.</>,
          <>Before anything costs credits, Claude names the price. {anwendung} cannot change your account or
        create API keys.</>,
        )}
        {scopes.length > 0 && <> {t('Angefragt:', 'Requested:')} {scopes.map(sc => t(SCOPE_TEXT, SCOPE_TEXT_EN)[sc] ?? sc).join(', ')}.</>}
      </p>

      <Knopf type="button" laedt={laeuft} onClick={() => void entscheiden('approve')}>{t('Erlauben', 'Allow')}</Knopf>
      <button
        type="button" disabled={laeuft} onClick={() => void entscheiden('deny')}
        className="mt-3 w-full cursor-pointer rounded-lg border border-linie bg-flaeche px-4 py-2.5 text-[0.9375rem] text-text-leise hover:border-text-hell hover:text-text disabled:opacity-60"
      >
        {t('Ablehnen', 'Deny')}
      </button>
    </Rahmen>
  );
}
